/**
 * Durations (screen time, caps, sessions) are stored as minutes everywhere. These helpers turn
 * minutes into "1 hr 30 min" for display and split/join the hours + minutes a parent types in.
 */

/** "45 min", "2 hr", "1 hr 30 min". Rounds to whole minutes; negatives keep a leading "-". */
export function formatDuration(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes)) return "0 min";
  const rounded = Math.round(totalMinutes);
  if (rounded < 0) return `-${formatDuration(-rounded)}`;
  const { hours, minutes } = splitMinutes(rounded);
  if (hours === 0) return `${minutes} min`;
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
}

/** Whole hours and the leftover 0–59 minutes of a non-negative total (rounded to whole minutes). */
export function splitMinutes(totalMinutes: number): { hours: number; minutes: number } {
  const total = Number.isFinite(totalMinutes) ? Math.max(0, Math.round(totalMinutes)) : 0;
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/**
 * Total minutes from an hours + minutes entry. Blank or invalid parts count as 0, hours are
 * whole and non-negative, and minutes are clamped to 0–59.
 */
export function joinDuration(hours: number | string, minutes: number | string): number {
  return Math.floor(nonNegative(hours)) * 60 + Math.min(59, Math.floor(nonNegative(minutes)));
}

/**
 * Parse a typed duration: "90", "1:30", "1h 30m", "1 hr 30 min", "2 hours", "45 min".
 * A bare number is minutes. Returns null when the text isn't a non-negative duration.
 */
export function parseDuration(text: string): number | null {
  const s = text.trim().toLowerCase();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(Number(s));
  const clock = /^(\d+):([0-5]\d)$/.exec(s);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const parts = /^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours))?\s*(?:(\d+)\s*(?:m|min|mins|minute|minutes))?$/.exec(s);
  if (!parts || (parts[1] === undefined && parts[2] === undefined)) return null;
  return Math.round(Number(parts[1] ?? 0) * 60 + Number(parts[2] ?? 0));
}

function nonNegative(v: number | string): number {
  const n = typeof v === "number" ? v : Number(String(v).trim() || 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
